const getGradientColor = (index) => {
    const colors = [
        '#F7FAFC',  // Lightest
        '#EDF2F7',
        '#E2E8F0',
        '#CBD5E0'   // Darkest
    ];
    return colors[index];
};

const formatAIResponse = (response, provider, theme) => {
    const cleanText = response
        .replace(/\[ROOT_CAUSE\]|\[WHY_ANALYSIS\]|\*\*/g, '')
        .trim();

    const template = `
        <div style="font-family: system-ui, -apple-system, sans-serif; line-height: 1.4">
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 15px; margin-bottom: 20px">
                <div style="background: ${theme.analysis.rootCause.background}; color: ${theme.analysis.rootCause.color}; border-radius: 8px; padding: 15px">
                    <h3 style="color: ${theme.analysis.rootCause.titleColor}; margin: 0 0 10px; font-size: 1.1rem">Root Cause Analysis</h3>
                    <div>${cleanText.split('Why 1:')[0]}</div>
                </div>
                ${[1,2,3,4,5].map(num => `
                    <div style="background: ${theme.analysis.whySection.background}; border: 1px solid ${theme.palette.divider}; border-radius: 8px; padding: 15px">
                        <h3 style="color: ${theme.analysis.whySection.titleColor}; margin: 0 0 10px">Why ${num}</h3>
                        <div style="display: grid; gap: 10px">
                            <div><strong style="color: ${theme.analysis.whySection.labelColor}">🎯 Problem</strong>
                                <div style="margin-top: 5px">${extractSection(cleanText, num, 'PROBLEM')}</div>
                            </div>
                            <div><strong style="color: ${theme.analysis.whySection.labelColor}">💡 Insight</strong>
                                <div style="margin-top: 5px">${extractSection(cleanText, num, 'INSIGHT')}</div>
                            </div>
                            <div><strong style="color: ${theme.analysis.whySection.labelColor}">✨ Position</strong>
                                <div style="margin-top: 5px">${extractSection(cleanText, num, 'POSITION')}</div>
                            </div>
                        </div>
                    </div>`).join('')}
            </div>
         <div style="display: flex; flex-direction: column; gap: 15px">
                ${['AUDIENCE', 'VALIDATION', 'STRATEGY', 'NEXT_STEPS'].map((section, index) => `
    <div style="background: ${theme.analysis.finalSections.background}; color: ${theme.analysis.finalSections.color}; padding: 20px; border-radius: 8px">
        <h3 style="color: ${theme.analysis.finalSections.titleColor}; margin: 0 0 15px">${section.replace('_', ' ')}</h3>
<ol style="margin: 0; padding-left: 20px; list-style-type: decimal">
    ${extractFinalSection(response, section)
        .split(/\d\.\s+/)
        .filter(item => item.trim())
        .map(item => `
            <li style="margin-bottom: 15px; padding-left: 10px; line-height: 1.5">
                ${item.trim()}
            </li>`)
        .join('')}
</ol>
    </div>`).join('')}
            </div>
        </div>`.replace(/\s+/g, ' ').trim();

    return {
        analysis: template,
        provider: provider
    };
};

const extractSection = (text, whyNum, type) => {
    
    const whyPattern = new RegExp(`Why ${whyNum}:[\\s\\S]*?(?=Why ${whyNum + 1}|\\[|$)`);
    const whySection = text.match(whyPattern)?.[0];
    
    const typePattern = new RegExp(`\\*\\s+${type}:\\s+(.*?)(?=\\*\\s+|$)`, 's');
    const match = whySection.match(typePattern);
    
    return match?.[1]?.trim() || '';
};

const extractFinalSection = (text, section) => {
    const sectionPattern = new RegExp(`\\[${section}\\][\\s\\S]*?(?=\\[|$)`);
    const sectionContent = text.match(sectionPattern)?.[0];
    
    if (sectionContent) {
        // Split on numbered items (1., 2., 3.)
        const items = sectionContent
            .replace(`[${section}]`, '')
            .replace(/\*\*/g, '')  // Remove all asterisks
            .split(/(?=\d+\.)/)
            .filter(item => item.trim());
            
        return items.join('\n');
    }
    return '';
};
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const HUGGINGFACE_API_KEY = process.env.REACT_APP_HUGGINGFACE_API_KEY;

const generateWithGemini = async (prompt) => {
    console.log('Sending to Gemini:', prompt);

    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }]
        })
    });
    const data = await response.json();
    console.log('Raw Gemini Response:', data);

    return data.candidates[0].content.parts[0].text;
};

const generateWithHuggingFace = async (prompt) => {
    const mistralPrompt = `<s>[INST] ${prompt} [/INST]`;
    
    const response = await fetch("https://api-inference.huggingface.co/models/mistralai/Mistral-7B-v0.1", {
        method: 'POST',
        headers: { 
            'Authorization': `Bearer ${HUGGINGFACE_API_KEY}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ inputs: mistralPrompt })
    });
    const data = await response.json();
    return data[0].generated_text.split('[/INST]')[1].trim();
};

export const generateCompleteAnalysis = async (context, answers, rawAnswers, theme, settings) => {
    
    // Ensure we're using the full answer strings
    const prompt = settings.aiContext
    .replace('{businessName}', context.businessName)
    .replace('{solution}', context.solution)
    .replace('{problemStatement}', answers)
    .replace(/\{answers\[(\d)\]\}/g, (match, index) => rawAnswers[index]);


        
    try {
        const response = settings.provider === 'huggingface'
            ? await generateWithHuggingFace(prompt)
            : await generateWithGemini(prompt);
        return formatAIResponse(response, settings.provider, theme);
    } catch (error) {
        console.error('AI Generation Error:', error);
        throw error;
    }
};
